import { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, MapPin, Calendar, Clock, Plus, Trash2, Mail } from "lucide-react";

/** Tarifs officiels de la soirée À Haute Voix */
export const HAV_PRICES = { orchestre: 60, balcon: 40 } as const;

const seatCategory = z.enum(["orchestre", "balcon"], {
  required_error: "Veuillez choisir une catégorie de place",
});

const guestSchema = z.object({
  firstName: z.string().trim().min(2, "Prénom requis").max(100),
  lastName: z.string().trim().min(2, "Nom requis").max(100),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
  email: z.string().trim().email("Adresse email invalide").max(255),
});

const havSchema = z.object({
  firstName: z.string().trim().min(2, "Prénom requis").max(100),
  lastName: z.string().trim().min(2, "Nom requis").max(100),
  phone: z.string().trim().min(6, "Téléphone requis").max(30),
  email: z.string().trim().email("Adresse email invalide").max(255),
  sponsorName: z.string().trim().min(3, "Nom et prénom du parrain requis").max(200),
  seatCategory,
  additionalDonation: z.coerce
    .number({ invalid_type_error: "Montant invalide" })
    .int("Montant invalide")
    .min(0, "Le don doit être positif")
    .max(1000000)
    .optional()
    .default(0),
  guests: z.array(guestSchema).max(20),
});

type HavFormValues = z.infer<typeof havSchema>;

const CATEGORY_LABEL: Record<"orchestre" | "balcon", string> = {
  orchestre: "Orchestre — 60 €",
  balcon: "Balcon — 40 €",
};

import fondAhv from "@/assets/fond-ahv-clean.png";

export default function HautVoixInscription() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  

  const form = useForm<HavFormValues>({
    resolver: zodResolver(havSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      sponsorName: "",
      seatCategory: undefined,
      additionalDonation: 0,
      guests: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "guests",
  });

  const values = form.watch();
  const seatsCount = 1 + (values.guests?.length || 0);
  const donation = Number(values.additionalDonation) || 0;
  const seatsTotal =
    (values.seatCategory ? HAV_PRICES[values.seatCategory] : 0) * seatsCount;
  const total = seatsTotal + donation;
  const seats = 1 + (values.guests?.length || 0);

  const onSubmit = async (data: HavFormValues) => {
    setIsSubmitting(true);
    try {
      const { data: inserted, error } = await supabase.rpc("insert_hav_registration", {
        p_first_name: data.firstName,
        p_last_name: data.lastName,
        p_email: data.email,
        p_phone: data.phone,
        p_sponsor_name: data.sponsorName,
        p_seat_category: data.seatCategory,
        p_additional_donation: data.additionalDonation || 0,
        p_guests: data.guests.map((g) => ({
          first_name: g.firstName,
          last_name: g.lastName,
          email: g.email,
          phone: g.phone && g.phone.trim() ? g.phone.trim() : null,
          seat_category: data.seatCategory,
        })),
      } as any);

      if (error) throw error;

      const reg = (inserted as any)?.[0] || inserted;

      const emailResult = await supabase.functions.invoke("send-hav-verification", {
        body: {
          registrationId: reg.id,
          verificationToken: reg.verification_token,
        },
      });

      if (emailResult.error) console.error("Email error:", emailResult.error);

      setIsSubmitted(true);
    } catch (err: any) {
      console.error("HAV registration error:", err);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Une erreur est survenue lors de la préinscription. Veuillez réessayer.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden bg-[#1E3A8A]"
      style={{
        backgroundImage: `url(${fondAhv})`,
        backgroundSize: "cover",
        backgroundPosition: "top center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "scroll",
      }}
    >

      <main className="relative z-10 container mx-auto px-4 py-6 md:py-10">
        {/* Bouton retour accueil */}
        <div className="flex justify-end mb-4 md:mb-6">
          <Link
            to="/"
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#0A1A6B] via-[#00C2F3] to-[#E5007E] px-4 py-2 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-105 hover:shadow-xl"
          >
            <span>←</span>
            <span>Retour à la page d'accueil</span>
          </Link>
        </div>

        {/* Header */}
        <header className="max-w-4xl mb-6 md:mb-10">
          <p className="text-white font-bold tracking-[0.2em] text-xs md:text-sm uppercase">
            La soirée caritative
          </p>
          <h1 className="mt-1 text-5xl md:text-7xl font-extrabold italic text-[#FFF200] uppercase leading-none">
            À Haute Voix
          </h1>
          <p className="text-white font-extrabold uppercase text-base md:text-xl mt-2 tracking-wide">
            Promouvoir notre socle civique
          </p>
          <p className="text-white/90 text-sm md:text-base mt-2 max-w-2xl">
            Animée par Fabrice Lundy, avec la participation d'acteurs engagés de la société civile.
          </p>
        </header>

        {/* Infos pratiques */}
        <div className="mb-6 md:mb-10 flex flex-wrap gap-4 md:gap-8 text-white font-semibold text-sm md:text-base">
          <span className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-[#FFF200]" /> Mercredi 25 novembre 2026
          </span>
          <span className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#FFF200]" /> 19h30 – 22h
          </span>
          <span className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#FFF200]" /> Théâtre de la Tour Eiffel — 4 Square Rapp, Paris 7ᵉ
          </span>
        </div>

        {/* Formulaire */}
        <div>
          <div className="w-full lg:w-1/2">
            <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm">
              <CardContent className="pt-6 pb-8">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-[#FFF200] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail className="h-8 w-8 text-[#0A1A6B]" />
                    </div>
                    <h2 className="text-2xl font-bold text-[#0A1A6B] mb-2">
                      Préinscription enregistrée — action requise !
                    </h2>
                    <div className="rounded-lg border-2 border-[#0A1A6B] bg-[#FFF200] p-5 my-4 text-center">
                      <p className="font-bold text-[#0A1A6B] text-lg mb-1">
                        Vérifiez votre boîte mail et procédez au paiement pour confirmer votre préinscription.
                      </p>
                      <p className="text-[#0A1A6B] font-medium">
                        Sans cette étape, votre place ne sera pas réservée.
                      </p>
                    </div>
                  </div>
                ) : (
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                      <div>
                        <h2 className="text-xl font-bold text-[#0A1A6B]">
                          Participant principal
                        </h2>
                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                          <FormField
                            control={form.control}
                            name="firstName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Prénom *</FormLabel>
                                <FormControl>
                                  <Input placeholder="Prénom" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="lastName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Nom *</FormLabel>
                                <FormControl>
                                  <Input placeholder="Nom" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="phone"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Téléphone *</FormLabel>
                                <FormControl>
                                  <Input type="tel" placeholder="06 12 34 56 78" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Email *</FormLabel>
                                <FormControl>
                                  <Input type="email" placeholder="votre@email.com" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="sponsorName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Nom et prénom du parrain *</FormLabel>
                                <FormControl>
                                  <Input placeholder="Ex : Marie Dupont" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="seatCategory"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Catégorie de place *</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Choisir une catégorie" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="orchestre">{CATEGORY_LABEL.orchestre}</SelectItem>
                                    <SelectItem value="balcon">{CATEGORY_LABEL.balcon}</SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      {/* Accompagnants */}
                      <div className="pt-4 border-t">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <h2 className="text-xl font-bold text-[#0A1A6B]">
                            Autres participants
                          </h2>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full sm:w-auto shrink-0"
                            onClick={() =>
                              append({
                                firstName: "",
                                lastName: "",
                                phone: "",
                                email: "",
                              })
                            }
                          >
                            <Plus className="h-4 w-4 mr-1 shrink-0" /> Ajouter une personne
                          </Button>
                        </div>

                        {fields.length === 0 && (
                          <p className="mt-2 text-sm text-muted-foreground">
                            Vous pouvez inscrire d'autres personnes ; tous les champs sont alors obligatoires.
                          </p>
                        )}

                        <div className="mt-4 space-y-6">
                          {fields.map((f, index) => (
                            <div key={f.id} className="rounded-lg border p-4 bg-muted/30">
                              <div className="flex items-center justify-between mb-3">
                                <p className="font-semibold text-[#0A1A6B]">
                                  Participant {index + 2}
                                </p>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => remove(index)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="grid gap-4 sm:grid-cols-2">
                                <FormField
                                  control={form.control}
                                  name={`guests.${index}.firstName`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Prénom *</FormLabel>
                                      <FormControl>
                                        <Input placeholder="Prénom" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={form.control}
                                  name={`guests.${index}.lastName`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Nom *</FormLabel>
                                      <FormControl>
                                        <Input placeholder="Nom" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={form.control}
                                  name={`guests.${index}.phone`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Téléphone</FormLabel>
                                      <FormControl>
                                        <Input type="tel" placeholder="06 12 34 56 78" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={form.control}
                                  name={`guests.${index}.email`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Email *</FormLabel>
                                      <FormControl>
                                        <Input type="email" placeholder="email@exemple.com" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <p className="sm:col-span-2 text-xs text-muted-foreground">
                                  Catégorie de place identique à celle du participant principal.
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Don supplémentaire */}
                      <div className="rounded-lg border border-[#0A1A6B]/20 bg-white p-4">
                        <FormField
                          control={form.control}
                          name="additionalDonation"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-[#0A1A6B] font-bold">
                                Je souhaite ajouter un don supplémentaire (optionnel)
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min={0}
                                  step={1}
                                  placeholder="0"
                                  {...field}
                                  value={field.value ?? 0}
                                  onChange={(e) =>
                                    field.onChange(
                                      e.target.value === "" ? 0 : Number(e.target.value)
                                    )
                                  }
                                />
                              </FormControl>
                              <p className="text-xs text-muted-foreground">
                                Ce don s'ajoute au prix des places et ouvre droit au reçu fiscal.
                              </p>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Récapitulatif */}
                      <div className="rounded-lg bg-[#FFF200]/90 p-4 text-center">
                        <p className="text-[#0A1A6B] font-semibold">
                          Total à régler :{" "}
                          <span className="text-xl font-bold">{total} €</span>
                        </p>
                        <p className="text-xs text-[#0A1A6B]/80 mt-1">
                          Un reçu fiscal (CERFA) du montant total vous sera adressé par email suite
                          à votre paiement et à la validation de l'inscription par le Bureau de
                          l'association.
                        </p>
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-6 bg-[#0A1A6B] hover:bg-[#06124d] text-white text-lg font-semibold"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Enregistrement en cours...
                          </span>
                        ) : (
                          "Je me préinscris"
                        )}
                      </Button>
                      <div className="bg-[#FFFEC4] border border-[#0A1A6B]/20 rounded-lg p-4 text-center">
                        <p className="text-sm font-semibold text-[#800020] leading-relaxed">
                          <strong className="block mb-1 uppercase tracking-wide">
                            LA PRÉINSCRIPTION EST PAYANTE
                          </strong>
                          Cliquez sur le bouton « Confirmer et Payer » dans l'email que vous allez recevoir
                          <br /><br />
                          En cas de non réception sur votre email, merci de vérifier dans vos SPAM.
                          <br /><br />
                          Votre inscription deviendra définitive après réception du paiement et
                          confirmation par le bureau de l'association.<br />
                          Si votre inscription ne pouvait être retenue, votre paiement vous serait
                          intégralement remboursé.
                        </p>
                      </div>
                    </form>
                  </Form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Bandeau rose bas de page */}
      <footer className="relative z-10 bg-[#F9A8DC] py-6 text-center text-[#0A1A6B] font-semibold">
        <p>Places limitées (400) - Pour toute information merci de nous envoyer un mail à contact@ledlr.fr</p>
      </footer>
    </div>
  );
}
